import React, { useMemo } from "react";
import {
  ArrowRight, FileAudio, FileSpreadsheet, Scale, Sparkles, TrendingUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatReportDate } from "@/utils/tlRecordingReportHelpers";
import { truncateReportText } from "@/utils/managerReportHelpers";
import SarOverviewAiInsight from "@/components/reports/SarOverviewAiInsight";

const CRITERIA_KEYS = [
  { key: "ordeleme", label: "İrdeleme" },
  { key: "mr_isteme", label: "MR İsteme" },
  { key: "mr_tepinme", label: "MR Tepinme" },
  { key: "tedirginlik", label: "Tedirginlik" },
  { key: "uzmanlik", label: "Uzmanlık" },
];

const MODULE_ACCENTS = {
  mola: { bar: "bg-amber-400", ring: "ring-amber-500/40", text: "text-amber-400" },
  mr: { bar: "bg-sky-400", ring: "ring-sky-500/40", text: "text-sky-400" },
  kaliteli: { bar: "bg-teal-400", ring: "ring-teal-500/40", text: "text-teal-400" },
  tl: { bar: "bg-violet-400", ring: "ring-violet-500/40", text: "text-violet-400" },
  tl_yeni: { bar: "bg-emerald-400", ring: "ring-emerald-500/40", text: "text-emerald-400" },
  kalite: { bar: "bg-blue-400", ring: "ring-blue-500/40", text: "text-blue-400" },
};

const normalizeYesNo = (value) => {
  if (value === true || value === 1 || value === "1") return "evet";
  if (value === false || value === 0 || value === "0") return "hayir";
  const v = String(value ?? "").toLocaleLowerCase("tr-TR").trim();
  if (!v || v === "—") return null;
  if (v === "evet" || v === "yes" || v === "true") return "evet";
  if (v === "hayir" || v === "hayır" || v === "no" || v === "false") return "hayir";
  return null;
};

const parsePercent = (value) => {
  if (value == null || value === "—") return null;
  const m = String(value).match(/([\d.,]+)\s*%/);
  if (!m) return null;
  return Math.min(100, Math.max(0, parseFloat(m[1].replace(",", "."))));
};

const yesNoLabel = (v) => {
  if (v === "evet") return "Evet";
  if (v === "hayir") return "Hayır";
  return "—";
};

function ScoreRing({ percent, size = 88, isDark, empty }) {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const p = empty || percent == null ? 0 : Math.min(100, Math.max(0, percent));
  const offset = c - (p / 100) * c;
  const stroke = empty || percent == null
    ? (isDark ? "#475569" : "#cbd5e1")
    : p >= 80
      ? "#34d399"
      : p >= 50
        ? "#fbbf24"
        : "#f87171";

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={isDark ? "rgba(51,65,85,0.8)" : "rgba(226,232,240,1)"}
          strokeWidth="6"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-xl font-bold tabular-nums ${isDark ? "text-white" : "text-gray-900"}`}>
          {empty || percent == null ? "—" : `%${percent}`}
        </span>
        <span className={`text-[9px] uppercase tracking-wider ${isDark ? "text-slate-500" : "text-gray-500"}`}>
          uyum
        </span>
      </div>
    </div>
  );
}

function MetricBars({ metrics, isDark, labelClass, maxBars = 4 }) {
  const bars = useMemo(() => {
    const out = [];
    (metrics || []).forEach((m) => {
      const pct = parsePercent(m.value);
      if (pct != null) {
        out.push({ label: m.label, pct, display: m.value });
      }
    });
    return out.slice(0, maxBars);
  }, [metrics, maxBars]);

  if (bars.length === 0) return null;

  return (
    <div className="space-y-2 mt-2">
      {bars.map((b) => (
        <div key={b.label}>
          <div className="flex justify-between gap-2 text-[10px] mb-0.5">
            <span className={labelClass}>{b.label}</span>
            <span className={`font-semibold tabular-nums ${isDark ? "text-slate-200" : "text-gray-800"}`}>
              {b.display}
            </span>
          </div>
          <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
            <div
              className="h-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-400 transition-all duration-700"
              style={{ width: `${b.pct}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

const MODULE_PULSE_TOOLTIPS = {
  mola: "Çubuk: mesai/mola modülündeki çalışma günü sayısına göre doluluk. Gün sayısı arttıkça sinyal yükselir.",
  mr: "Çubuk: MR isteme oranlarının ortalaması (%). Metriklerden yüzde değerleri okunur.",
  kaliteli: "Çubuk: kaliteli data modülündeki yüzde metriklerinin ortalaması.",
  tl: "Çubuk: Tk. lideri ses kaydı raporunda dolu kalan özet metrik sayısı (boş/— hariç).",
  kalite: "Çubuk: kalite değerlendirme özetinde dolu metrik sayısı.",
  tl_yeni: "Çubuk: yeni TL form kayıt sayısı. Kayıt varsa yüksek, yoksa düşük sinyal.",
};

function computeModulePulse(section) {
  const tabId = section.tabId;
  const tooltip = MODULE_PULSE_TOOLTIPS[tabId] || "Modül verisine göre doluluk sinyali.";
  if (section.empty || section.placeholder) {
    return { score: 0, label: "Veri yok", tooltip: "Bu modül için seçili dönemde veri yok." };
  }
  const metrics = section.metrics || [];
  if (tabId === "mr" || tabId === "kaliteli") {
    const ratios = metrics.map((m) => parsePercent(m.value)).filter((p) => p != null);
    if (ratios.length) {
      const avg = Math.round(ratios.reduce((a, b) => a + b, 0) / ratios.length);
      return { score: avg, label: `%${avg} ort.`, tooltip };
    }
  }
  if (tabId === "mola") {
    const wd = parseInt(metrics.find((m) => m.label === "Çalışma günü")?.value, 10);
    if (!Number.isNaN(wd) && wd > 0) {
      return { score: Math.min(100, wd * 12), label: `${wd} gün`, tooltip };
    }
  }
  if (tabId === "tl" || tabId === "kalite") {
    const filled = metrics.filter((m) => m.value && m.value !== "—" && m.value !== "Yok").length;
    return { score: Math.min(100, 35 + filled * 12), label: `${filled} metrik`, tooltip };
  }
  if (tabId === "tl_yeni") {
    const n = parseInt(metrics.find((m) => m.label === "Form kaydı")?.value, 10);
    return { score: n > 0 ? 85 : 40, label: n > 0 ? `${n} kayıt` : "Kayıt", tooltip };
  }
  return { score: 55, label: "Aktif", tooltip };
}

const TlKaliteHero = ({ apiBase, bundle, alignment, isDark, textClass, labelClass, onOpenTab }) => {
  const compare = useMemo(() => {
    const tlItems = bundle?.tl_recordings?.items || [];
    const kaliteItems = bundle?.kalite_degerlendirme?.items || [];
    if (!tlItems.length || !kaliteItems.length) return null;

    const byDate = new Map(kaliteItems.map((r) => [String(r.report_date || "").slice(0, 10), r]));
    let tlRow = null;
    let kaliteRow = null;
    for (const t of tlItems) {
      const k = String(t.report_date || "").slice(0, 10);
      if (k && byDate.has(k)) {
        tlRow = t;
        kaliteRow = byDate.get(k);
        break;
      }
    }
    if (!tlRow) {
      tlRow = tlItems[0];
      kaliteRow = kaliteItems[0];
    }

    const criteria = CRITERIA_KEYS.map(({ key, label }) => {
      const tlVal = normalizeYesNo(tlRow[key]);
      const kVal = normalizeYesNo(kaliteRow[key]);
      const match = tlVal != null && kVal != null && tlVal === kVal;
      return { key, label, tlVal, kVal, match };
    });

    return {
      tlRow,
      kaliteRow,
      criteria,
      date: tlRow.report_date ? formatReportDate(tlRow.report_date) : "—",
    };
  }, [bundle]);

  if (!compare && !alignment) return null;

  return (
    <div className={cn("sar-v2-tl-kalite-hero rounded-2xl border p-4 md:p-5", isDark ? "border-teal-500/30 bg-slate-900/50" : "border-teal-200 bg-gradient-to-br from-white to-teal-50/80")}>
      <div className="flex flex-col lg:flex-row gap-5 lg:gap-8">
        <div className="flex flex-col gap-4 shrink-0 lg:w-[min(100%,400px)]">
          <div className="flex items-start gap-4">
            <ScoreRing
              percent={alignment?.percent}
              isDark={isDark}
              empty={alignment?.percent == null}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Scale className={cn("w-4 h-4", isDark ? "text-teal-400" : "text-teal-600")} />
                <h3 className={cn("text-sm font-bold tracking-tight", textClass)}>Takım Lideri × Kalite</h3>
              </div>
              <p className={cn("text-[11px] leading-relaxed", labelClass)}>
                {alignment?.message || "Karşılaştırma için her iki raporda da kayıt gerekir."}
              </p>
              {compare?.date ? (
                <p className={cn("text-[10px] mt-2 font-medium", isDark ? "text-teal-300/80" : "text-teal-700")}>
                  Eşleşen dönem: {compare.date}
                </p>
              ) : null}
            </div>
          </div>
          <SarOverviewAiInsight
            apiBase={apiBase}
            bundle={bundle}
            tlKaliteAlignment={alignment}
            enabled={!!compare && !!alignment}
            isDark={isDark}
            textClass={textClass}
            labelClass={labelClass}
          />
        </div>

        {compare ? (
          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-2 gap-y-1 text-[10px] font-semibold uppercase tracking-wide mb-2 px-1">
              <span className={cn("flex items-center gap-1", isDark ? "text-violet-300" : "text-violet-700")}>
                <FileAudio className="w-3 h-3" /> Tk. Lideri
              </span>
              <span className={labelClass}>Kriter</span>
              <span className={cn("flex items-center gap-1 justify-end", isDark ? "text-blue-300" : "text-blue-700")}>
                Kalite <FileSpreadsheet className="w-3 h-3" />
              </span>
            </div>
            <div className="space-y-1.5">
              {compare.criteria.map((c) => {
                const hasBoth = c.tlVal != null && c.kVal != null;
                return (
                  <div
                    key={c.key}
                    className={cn(
                      "grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-lg px-2 py-1.5 text-xs",
                      hasBoth && !c.match
                        ? (isDark ? "bg-rose-500/10 ring-1 ring-rose-500/25" : "bg-rose-50 ring-1 ring-rose-200")
                        : hasBoth && c.match
                          ? (isDark ? "bg-emerald-500/8 ring-1 ring-emerald-500/20" : "bg-emerald-50/90 ring-1 ring-emerald-200/80")
                          : (isDark ? "bg-slate-800/40" : "bg-white/80"),
                    )}
                  >
                    <span className={cn("font-medium tabular-nums", isDark ? "text-slate-200" : "text-gray-800")}>
                      {yesNoLabel(c.tlVal)}
                    </span>
                    <span className={cn("text-[10px] text-center px-1", labelClass)}>{c.label}</span>
                    <span className={cn("font-medium tabular-nums text-right", isDark ? "text-slate-200" : "text-gray-800")}>
                      {yesNoLabel(c.kVal)}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <div className={cn("rounded-lg p-2.5 border-l-2", isDark ? "border-violet-500 bg-slate-950/40" : "border-violet-400 bg-white")}>
                <p className={cn("text-[10px] font-semibold mb-1", isDark ? "text-violet-300" : "text-violet-800")}>TL yorum özeti</p>
                <p className={cn("text-[11px] leading-relaxed line-clamp-4", textClass)}>
                  {truncateReportText(compare.tlRow.leader_report || compare.tlRow.assistant_report || "—", 220)}
                </p>
              </div>
              <div className={cn("rounded-lg p-2.5 border-l-2", isDark ? "border-blue-500 bg-slate-950/40" : "border-blue-400 bg-white")}>
                <p className={cn("text-[10px] font-semibold mb-1", isDark ? "text-blue-300" : "text-blue-800")}>Kalite analiz özeti</p>
                <p className={cn("text-[11px] leading-relaxed line-clamp-4", textClass)}>
                  {truncateReportText(compare.kaliteRow.call1_evaluation || compare.kaliteRow.call2_evaluation || "—", 220)}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={() => onOpenTab?.("tl")}
                className={cn("text-[10px] font-medium px-2 py-1 rounded-md", isDark ? "bg-violet-500/20 text-violet-200 hover:bg-violet-500/30" : "bg-violet-100 text-violet-800")}
              >
                Ses kayıtları detay
              </button>
              <button
                type="button"
                onClick={() => onOpenTab?.("kalite")}
                className={cn("text-[10px] font-medium px-2 py-1 rounded-md", isDark ? "bg-blue-500/20 text-blue-200 hover:bg-blue-500/30" : "bg-blue-100 text-blue-800")}
              >
                Kalite detay
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ModulePulseRail = ({ sections, isDark, labelClass, onOpenTab }) => (
  <div className={cn("sar-v2-pulse-rail rounded-xl border p-3", isDark ? "border-slate-700/80 bg-slate-900/35" : "border-gray-200 bg-white/90")}>
    <div className="flex items-center gap-2 mb-3">
      <TrendingUp className={cn("w-4 h-4", isDark ? "text-teal-400" : "text-teal-600")} />
      <span className={cn("text-xs font-semibold", isDark ? "text-slate-200" : "text-gray-800")}>6 modül özeti</span>
      <span className={cn("text-[10px]", labelClass)}>— doluluk / oran sinyali</span>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
      {sections.map((section) => {
        const pulse = computeModulePulse(section);
        const accent = MODULE_ACCENTS[section.tabId] || MODULE_ACCENTS.mola;
        const Icon = section.icon;
        return (
          <button
            key={section.tabId}
            type="button"
            disabled={section.placeholder}
            title={pulse.tooltip}
            aria-label={`${section.title}: ${pulse.label}. ${pulse.tooltip}`}
            onClick={() => !section.placeholder && onOpenTab?.(section.tabId)}
            className={cn(
              "sar-v2-pulse-cell text-left rounded-lg p-2.5 ring-1 transition-all",
              accent.ring,
              isDark ? "bg-slate-800/50 hover:bg-slate-800/80" : "bg-gray-50 hover:bg-white hover:shadow-sm",
              section.placeholder && "opacity-50 cursor-default",
            )}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Icon className={cn("w-3.5 h-3.5", accent.text)} />
              <span className={cn("text-[10px] font-semibold leading-tight line-clamp-2", isDark ? "text-slate-200" : "text-gray-800")}>
                {section.title.replace("Tk. ", "").replace("Takım Lideri ", "TL ")}
              </span>
            </div>
            <div className={cn("h-1.5 rounded-full overflow-hidden mb-1", isDark ? "bg-slate-900" : "bg-gray-200")}>
              <div className={cn("h-full rounded-full transition-all duration-700", accent.bar)} style={{ width: `${pulse.score}%` }} />
            </div>
            <span className={cn("text-[9px] font-medium tabular-nums", labelClass)}>{pulse.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const OverviewMetric = ({ label, value, isDark, labelClass }) => (
  <div className="flex justify-between gap-3 text-xs">
    <span className={labelClass}>{label}</span>
    <span className={`font-medium text-right ${isDark ? "text-slate-200" : "text-gray-900"}`}>{value}</span>
  </div>
);

const OverviewTextBlock = ({ label, value, isDark, textClass, layoutClass }) => (
  <div className={layoutClass}>
    <p className={`text-[10px] font-semibold mb-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{label}</p>
    <p className={`text-[11px] leading-relaxed whitespace-pre-wrap break-words ${textClass}`}>{value}</p>
  </div>
);

const SpecialAgentOverviewV2 = ({
  apiBase,
  sections,
  bundle,
  tlKaliteAlignment,
  isDark,
  textClass,
  labelClass,
  onOpenTab,
  onKaliteliBatchChange,
  kaliteliBatchSelect,
}) => {
  const activeModules = sections.filter((s) => !s.empty && !s.placeholder).length;

  return (
    <div className="space-y-5 sar-tab-enter">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <p className={`text-xs sar-v2-overview-intro ${labelClass}`}>
          Sunum modu — TL ile Kalite karşılaştırması öne çıkar; modül kartlarında oran grafikleri.
        </p>
        <div className={cn("sar-v2-badge flex items-center gap-1.5", isDark ? "" : "")}>
          <Sparkles className="w-3 h-3" />
          {activeModules}/6 modülde veri
        </div>
      </div>

      <TlKaliteHero
        apiBase={apiBase}
        bundle={bundle}
        alignment={tlKaliteAlignment}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        onOpenTab={onOpenTab}
      />

      <ModulePulseRail sections={sections} isDark={isDark} labelClass={labelClass} onOpenTab={onOpenTab} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {sections.map((section, sectionIndex) => {
          const Icon = section.icon;
          const accent = MODULE_ACCENTS[section.tabId] || MODULE_ACCENTS.mola;
          return (
            <button
              key={section.tabId}
              type="button"
              onClick={() => !section.placeholder && onOpenTab(section.tabId)}
              disabled={section.placeholder}
              style={{ animationDelay: `${sectionIndex * 90}ms` }}
              className={cn(
                "sar-overview-card sar-v2-overview-card text-left rounded-xl border p-4",
                section.placeholder ? "opacity-75 cursor-default" : "",
              )}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={cn("p-1.5 rounded-lg shrink-0 ring-1", accent.ring, isDark ? "bg-slate-900/80" : "bg-white")}>
                    <Icon className={cn("w-4 h-4", accent.text)} />
                  </div>
                  <h4 className={`text-sm font-semibold leading-snug ${textClass}`}>{section.title}</h4>
                </div>
                {!section.placeholder && (
                  <span className={`inline-flex items-center text-[10px] font-medium shrink-0 ${isDark ? "text-teal-400" : "text-teal-700"}`}>
                    Detay <ArrowRight className="w-3 h-3 ml-0.5" />
                  </span>
                )}
              </div>

              {section.tabId === "kaliteli" && kaliteliBatchSelect}

              <div className="space-y-1.5">
                {section.metrics.slice(0, 5).map((m) => (
                  <OverviewMetric key={`${section.tabId}-${m.label}`} {...m} isDark={isDark} labelClass={labelClass} />
                ))}
              </div>

              <MetricBars metrics={section.metrics} isDark={isDark} labelClass={labelClass} />

              {section.textBlocks?.length > 0 && (
                <div
                  className={`border-t pt-2 mt-2 space-y-2 ${
                    section.textLayout === "split2"
                      ? "grid grid-cols-1 sm:grid-cols-2 gap-3"
                      : section.textLayout === "split3"
                        ? "grid grid-cols-1 sm:grid-cols-3 gap-3"
                        : ""
                  } ${isDark ? "border-slate-700/80" : "border-gray-200/80"}`}
                >
                  {section.textBlocks.slice(0, section.textLayout === "split3" ? 3 : 2).map((block) => (
                    <OverviewTextBlock
                      key={`${section.tabId}-${block.label}`}
                      label={block.label}
                      value={block.value}
                      isDark={isDark}
                      textClass={textClass}
                      layoutClass={`rounded-md p-2 min-h-[3.5rem] ${isDark ? "bg-slate-900/50" : "bg-white/80"}`}
                    />
                  ))}
                </div>
              )}

              {section.insight && (
                <p className={`text-[11px] leading-relaxed border-t pt-2 mt-2 ${isDark ? "border-slate-700/80 text-slate-400" : "border-gray-200/80 text-gray-600"}`}>
                  {section.insight}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SpecialAgentOverviewV2;
